/** 协同委外库存汇总看板 */

import { parseWarehouseSpec } from '@/utils/outsourcingWarehouse'

export function formatInventoryQty(value) {
  if (value === undefined || value === null || value === '') return 0
  const num = Number(value)
  if (Number.isNaN(num)) return 0
  return Math.round(num * 1000) / 1000
}

export function formatInventoryQtyLabel(value) {
  const num = formatInventoryQty(value)
  return Number.isInteger(num)
    ? String(num)
    : String(num)
}

/** 看板四卡：账面总库存 / 产品委外 / 工序委外 / 批次号数量 */
export function normalizeInventoryStatCards(source = {}) {
  const totalQuantity = formatInventoryQty(source.totalQuantity)
  const materialTypeCount = Number(source.materialTypeCount ?? 0) || 0
  const productQuantity = formatInventoryQty(source.productQuantity)
  const processQuantity = formatInventoryQty(source.processQuantity)
  const batchedQuantity = formatInventoryQty(source.batchedQuantity)
  const productRatio =
    totalQuantity > 0
      ? Math.round((productQuantity / totalQuantity) * 100)
      : 0
  const processRatio =
    totalQuantity > 0
      ? Math.round((processQuantity / totalQuantity) * 100)
      : 0
  const batchedRatio =
    totalQuantity > 0
      ? Math.round((batchedQuantity / totalQuantity) * 100)
      : 0

  return [
    {
      key: 'book',
      title: '账面总库存',
      count: formatInventoryQtyLabel(totalQuantity),
      desc: `覆盖 ${materialTypeCount} 个主物料，含产品与工序全部结存`,
      color: '#303133',
      accent: '#1476c3',
      tone: 'primary',
    },
    {
      key: 'product',
      title: '产品委外数量',
      count: formatInventoryQtyLabel(productQuantity),
      desc: `产品委外结存，约占总量 ${productRatio}%`,
      color: '#409eff',
      accent: '#409eff',
      tone: 'info',
    },
    {
      key: 'process',
      title: '工序委外数量',
      count: formatInventoryQtyLabel(processQuantity),
      desc: `工序委外结存，约占总量 ${processRatio}%`,
      color: '#e6a23c',
      accent: '#e6a23c',
      tone: 'warning',
    },
    {
      key: 'batched',
      title: '批次号数量',
      count: formatInventoryQtyLabel(batchedQuantity),
      desc: `已维护批次号的库存合计，约占总量 ${batchedRatio}%`,
      color: '#67c23a',
      accent: '#67c23a',
      tone: 'success',
    },
  ]
}

export function buildInventoryStatPayload({ searchString = '', bizDocKind } = {}) {
  const filter = {
    searchString: searchString || '',
  }
  if (bizDocKind !== null && bizDocKind !== undefined && bizDocKind !== '') {
    filter.bizDocKind = Number(bizDocKind)
  } else {
    filter.bizDocKind = null
  }
  return {
    outsourcingWarehouseFilter: filter,
  }
}

export function buildMaterialSummaryPayload({
  searchString = '',
  bizDocKind,
  pageParam = { pageNum: 1, pageSize: 20 },
} = {}) {
  return {
    ...buildInventoryStatPayload({ searchString, bizDocKind }),
    pageParam: {
      pageNum: pageParam.pageNum || 1,
      pageSize: pageParam.pageSize || 20,
    },
  }
}

export function formatMaterialSummaryLabel(row = {}) {
  const name = row.baseMaterialName || row.baseMaterialNumber || '未命名物料'
  const specRow = parseWarehouseSpec({
    baseSpecgroupDesc: row.baseSpecgroupDesc,
  })
  const specList = specRow.materialSpecList || []
  let specText = ''
  if (specList.length) {
    specText = specList
      .map((item) => {
        if (typeof item === 'string') return item
        const key = item.specKey || ''
        const value = item.specValue || ''
        if (key && value) return `${key}:${value}`
        return value || key || ''
      })
      .filter(Boolean)
      .join('/')
  } else if (
    row.baseSpecgroupDesc &&
    !String(row.baseSpecgroupDesc).trim().startsWith('[')
  ) {
    specText = row.baseSpecgroupDesc
  }
  return specText ? `${name}（${specText}）` : name
}

export function normalizeMaterialSummaryList(list = []) {
  return (list || []).map((item, index) => {
    const qty = formatInventoryQty(item.totalQuantity)
    return {
      ...item,
      _index: index,
      _label: formatMaterialSummaryLabel(item),
      _qty: qty,
      _qtyLabel: formatInventoryQtyLabel(qty),
    }
  })
}

const CHART_COLORS = ['#409eff', '#67c23a', '#e6a23c', '#909399', '#f56c6c', '#909399']

export function calcRatio(part, total) {
  const p = formatInventoryQty(part)
  const t = formatInventoryQty(total)
  if (t <= 0) return 0
  const ratio = Math.round((p / t) * 1000) / 10
  return Math.min(100, Math.max(0, ratio))
}

/** 右侧库存构成：产品/工序、有无批次、四象限明细 */
export function normalizeInventoryStructure(source = {}) {
  const totalQuantity = formatInventoryQty(source.totalQuantity)
  const productQuantity = formatInventoryQty(source.productQuantity)
  const processQuantity = formatInventoryQty(source.processQuantity)
  const batchedQuantity = formatInventoryQty(source.batchedQuantity)
  const unbatchedQuantity = Math.max(
    0,
    formatInventoryQty(
      source.unbatchedQuantity != null
        ? source.unbatchedQuantity
        : totalQuantity - batchedQuantity
    )
  )

  const productBatchedQuantity = formatInventoryQty(source.productBatchedQuantity)
  const productUnbatchedQuantity = formatInventoryQty(
    source.productUnbatchedQuantity != null
      ? source.productUnbatchedQuantity
      : Math.max(0, productQuantity - productBatchedQuantity)
  )
  const processBatchedQuantity = formatInventoryQty(source.processBatchedQuantity)
  const processUnbatchedQuantity = formatInventoryQty(
    source.processUnbatchedQuantity != null
      ? source.processUnbatchedQuantity
      : Math.max(0, processQuantity - processBatchedQuantity)
  )

  return {
    totalQuantity,
    bizKindRows: [
      {
        key: 'product',
        label: '产品委外',
        value: productQuantity,
        ratio: calcRatio(productQuantity, totalQuantity),
        color: '#409eff',
      },
      {
        key: 'process',
        label: '工序委外',
        value: processQuantity,
        ratio: calcRatio(processQuantity, totalQuantity),
        color: '#e6a23c',
      },
    ],
    batchRows: [
      {
        key: 'batched',
        label: '有批次号',
        value: batchedQuantity,
        ratio: calcRatio(batchedQuantity, totalQuantity),
        color: '#67c23a',
      },
      {
        key: 'unbatched',
        label: '无批次号',
        value: unbatchedQuantity,
        ratio: calcRatio(unbatchedQuantity, totalQuantity),
        color: '#909399',
      },
    ],
    quadrantRows: [
      {
        key: 'productBatched',
        name: '产品·有批次',
        value: productBatchedQuantity,
        color: '#409eff',
      },
      {
        key: 'productUnbatched',
        name: '产品·无批次',
        value: productUnbatchedQuantity,
        color: '#79bbff',
      },
      {
        key: 'processBatched',
        name: '工序·有批次',
        value: processBatchedQuantity,
        color: '#e6a23c',
      },
      {
        key: 'processUnbatched',
        name: '工序·无批次',
        value: processUnbatchedQuantity,
        color: '#f3d19e',
      },
    ],
  }
}

export function buildStructureDonutOption(rows = [], title = '') {
  const data = (rows || [])
    .map((item) => ({
      name: item.name || item.label,
      value: formatInventoryQty(item.value),
      itemStyle: { color: item.color },
    }))
    .filter((item) => item.value > 0)

  const hasData = data.length > 0
  return {
    tooltip: {
      trigger: 'item',
      formatter: ({ name, value, percent }) =>
        `${name}<br/>数量：${formatInventoryQtyLabel(value)}（${percent}%）`,
    },
    legend: {
      show: false,
    },
    series: [
      {
        type: 'pie',
        radius: ['48%', '72%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: true,
        label: {
          show: false,
        },
        labelLine: { show: false },
        data: hasData
          ? data
          : [
              {
                name: '暂无数据',
                value: 1,
                itemStyle: { color: '#ebeef5' },
                tooltip: { show: false },
              },
            ],
      },
    ],
    title: title
      ? {
          text: title,
          left: 'center',
          top: 'center',
          textStyle: {
            color: '#606266',
            fontSize: 12,
            fontWeight: 600,
          },
        }
      : undefined,
  }
}

/** Y 轴短标签，完整名称走 tooltip */
function truncateAxisLabel(text, maxLen = 14) {
  const s = String(text || '')
  if (s.length <= maxLen) return s
  return `${s.slice(0, maxLen)}…`
}

export function buildMaterialSummaryChartOption(rows = []) {
  const labels = rows.map((item) => item._label)
  const values = rows.map((item) => item._qty)
  const colors = rows.map((_, index) => CHART_COLORS[index % CHART_COLORS.length])
  const chartHeight = Math.max(220, rows.length * 44 + 40)

  return {
    chartHeight,
    option: {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params) => {
          const item = Array.isArray(params) ? params[0] : params
          if (!item) return ''
          return `${item.name}<br/>数量：${formatInventoryQtyLabel(item.value)}`
        },
      },
      grid: {
        left: 120,
        right: 48,
        top: 12,
        bottom: 20,
        containLabel: false,
      },
      xAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: '#ebeef5' } },
      },
      yAxis: {
        type: 'category',
        data: labels,
        inverse: true,
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: {
          color: '#606266',
          width: 108,
          overflow: 'truncate',
          ellipsis: '…',
          formatter: (value) => truncateAxisLabel(value, 14),
        },
      },
      series: [
        {
          type: 'bar',
          data: values.map((value, index) => ({
            value,
            itemStyle: { color: colors[index], barBorderRadius: [0, 4, 4, 0] },
          })),
          barWidth: 18,
          label: {
            show: true,
            position: 'right',
            color: '#606266',
            formatter: ({ value }) => formatInventoryQtyLabel(value),
          },
        },
      ],
    },
  }
}
